import { useRef, useState, type MouseEvent } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import {
  ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
  ELEVATED_DIALOG_Z_INDEX_OFFSET,
} from "../../../constants/dialogZIndex";
import {
  formatPlanTypeCode,
  type AutoTaggingPlan,
  type AutoTaggingPlanReferenceItem,
} from "../../../services/autoTaggingService";

export type ReferenceItemDetail = {
  label: string;
  object: string;
  dimension: string;
  value: string;
};

function formatDimensionValues(item: AutoTaggingPlanReferenceItem): string {
  if (item.all_values) return "All";
  return item.values
    .map((value) => value.name.trim() || value.code)
    .filter(Boolean)
    .join(", ");
}

/**
 * Build Start-dialog Reference Items cards (R1, R2, …) from plan
 * `reference_items` + `target_type` as Object.
 */
export function buildPlanReferenceItemDetails(
  plan: AutoTaggingPlan,
): ReferenceItemDetail[] {
  const items = plan.reference_items ?? [];
  if (items.length === 0) return [];

  const objectName =
    formatPlanTypeCode(plan.target_type) || plan.target_type.trim() || "";

  return items.map((item, index) => ({
    label: `R${index + 1}`,
    object: objectName,
    dimension: item.display_name?.trim() || item.concept.trim() || "",
    value: formatDimensionValues(item),
  }));
}

/** Cell trigger text: `R1, R2`. */
export function formatReferenceItemsRnLabel(
  details: readonly ReferenceItemDetail[],
): string {
  return details.map((item) => item.label).join(", ");
}

type ReferenceItemsRnCellProps = Readonly<{
  details: readonly ReferenceItemDetail[];
}>;

/**
 * Dashed `R1, R2` trigger + hover popover (Object / Dimension / Value).
 * Same pattern as AI Hub Classification Plan Reference Items.
 */
export function ReferenceItemsRnCell({ details }: ReferenceItemsRnCellProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const label = formatReferenceItemsRnLabel(details);
  const open = Boolean(anchorEl) && details.length > 0;

  const clearCloseTimer = () => {
    if (closeTimerRef.current != null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (details.length === 0) return;
    clearCloseTimer();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSoon = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setAnchorEl(null);
      closeTimerRef.current = null;
    }, 120);
  };

  if (!label) {
    return (
      <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>-</Typography>
    );
  }

  const title = `${details.length} Reference ${
    details.length === 1 ? "Item" : "Items"
  }`;

  return (
    <>
      <Box
        component="span"
        onMouseEnter={handleOpen}
        onMouseLeave={handleCloseSoon}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          maxWidth: "100%",
          minWidth: 0,
          cursor: "default",
          color: "#364153",
          fontSize: "14px",
          lineHeight: "16px",
          borderBottom: "1px dashed #99A1AF",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          "&:hover": {
            color: "#295EF1",
            borderBottomColor: "#295EF1",
          },
        }}
      >
        {label}
      </Box>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[
          { name: "offset", options: { offset: [0, 6] } },
          { name: "preventOverflow", options: { padding: 8 } },
        ]}
        sx={{
          zIndex: (theme) =>
            theme.zIndex.modal +
            ELEVATED_DIALOG_Z_INDEX_OFFSET +
            ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
        }}
      >
        <Paper
          elevation={0}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={handleCloseSoon}
          sx={{
            width: 280,
            borderRadius: "8px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 8px 24px rgba(25, 37, 50, 0.16)",
            p: "12px 14px",
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#101828",
              mb: "10px",
            }}
          >
            {title}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {details.map((item) => (
              <Box
                key={item.label}
                sx={{
                  backgroundColor: "#F9FAFB",
                  borderRadius: "4px",
                  p: "8px 10px",
                  display: "flex",
                  gap: "10px",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    flex: "none",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#295EF1",
                    backgroundColor: "#E7ECFC",
                    borderRadius: "3px",
                    px: "6px",
                    height: "18px",
                    lineHeight: "18px",
                  }}
                >
                  {item.label}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "grid",
                    gridTemplateColumns: "64px minmax(0, 1fr)",
                    rowGap: "4px",
                    columnGap: "8px",
                    fontSize: "12px",
                  }}
                >
                  <Box component="span" sx={{ color: "#6A7282" }}>
                    Object
                  </Box>
                  <Box component="span" sx={{ color: "#0D0D0D" }}>
                    {item.object || "—"}
                  </Box>
                  <Box component="span" sx={{ color: "#6A7282" }}>
                    Dimension
                  </Box>
                  <Box component="span" sx={{ color: "#0D0D0D" }}>
                    {item.dimension || "—"}
                  </Box>
                  <Box component="span" sx={{ color: "#6A7282" }}>
                    Value
                  </Box>
                  <Box
                    component="span"
                    sx={{ color: "#0D0D0D", fontWeight: 500 }}
                  >
                    {item.value || "—"}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      </Popper>
    </>
  );
}

type ReferenceItemsCellProps = Readonly<{
  plan: AutoTaggingPlan;
}>;

/** Start Auto-tagging / Run AI Assessment Reference Items cell. */
export default function ReferenceItemsCell({ plan }: ReferenceItemsCellProps) {
  return (
    <ReferenceItemsRnCell details={buildPlanReferenceItemDetails(plan)} />
  );
}
